import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import useNativeSetup from './hooks/useNativeSetup';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';
import LoadingSpinner from './components/LoadingSpinner';
import { initialize as initPayments } from './services/payments';
import { useActiveUser } from './hooks/useActiveUser';
import useSubscriptionSync from './hooks/useSubscriptionSync';
import { useSyncEngine } from './services/sync/engine';

import Record from './pages/Record';

const DaySummary = lazy(() => import('./pages/DaySummary'));
const SessionDetail = lazy(() => import('./pages/SessionDetail'));
const Logbook = lazy(() => import('./pages/Logbook'));
const SeasonStats = lazy(() => import('./pages/SeasonStats'));
const Resort = lazy(() => import('./pages/Resort'));
const ResortBrowser = lazy(() => import('./pages/ResortBrowser'));
const Friends = lazy(() => import('./pages/Friends'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const MapView = lazy(() => import('./pages/MapView'));
const Settings = lazy(() => import('./pages/Settings'));
const Premium = lazy(() => import('./pages/Premium'));

const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const E2EAuth = lazy(() => import('./pages/E2EAuth'));
const NotFound = lazy(() => import('./pages/NotFound'));

function PageFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-night-bg">
      <LoadingSpinner size="lg" />
    </div>
  );
}

export default function App() {
  useNativeSetup();
  const { userId, isAuthed, isAnonymous } = useActiveUser();
  useSyncEngine({ userId, isAuthed, isAnonymous });
  // Server truth for premium gating — the optimistic IAP grant is provisional.
  useSubscriptionSync({ userId, isAuthed });

  useEffect(() => {
    initPayments().catch((err) => console.warn('payments init failed:', err));
  }, []);

  return (
    <>
      <Toaster
        position="top-center"
        containerStyle={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
        toastOptions={{
          duration: 3500,
          style: {
            borderRadius: '14px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: 500,
            maxWidth: '92vw',
            background: 'var(--toast-bg)',
            color: 'var(--toast-fg)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
            border: '1px solid var(--toast-border)',
          },
          success: { iconTheme: { primary: 'var(--app-accent)', secondary: '#fff' }, duration: 2500 },
          error: { iconTheme: { primary: '#ef4444', secondary: '#fff' }, duration: 4500 },
        }}
      />
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
          </Route>

          {/* Anonymous-accessible app. Sessions stay local until sign-in. */}
          <Route element={<Layout />}>
            <Route path="/" element={<Record />} />
            <Route path="/summary/:sessionId" element={<DaySummary />} />
            <Route path="/session/:sessionId" element={<SessionDetail />} />
            <Route path="/logbook" element={<Logbook />} />
            <Route path="/stats" element={<SeasonStats />} />
            <Route path="/resort/:resortId" element={<Resort />} />
            <Route path="/resorts" element={<ResortBrowser />} />
            <Route path="/map" element={<MapView />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/premium" element={<Premium />} />

            {/* Social routes still require Cognito — PrivateRoute renders an
                interstitial CTA for anonymous users. */}
            <Route element={<PrivateRoute />}>
              <Route path="/friends" element={<Friends />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
            </Route>
          </Route>

          <Route path="/auth/callback" element={<OAuthCallback />} />
          <Route path="/e2e-auth" element={<E2EAuth />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
